import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { browserSupportsWebAuthn } from '@simplewebauthn/browser';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { KEYS } from '../../api/keys';
import { deviceLabel } from '../../lib/device';
import { passkeyErrorText, registerDevice, signIn } from './passkeys';

export function LoginPage() {
  const qc = useQueryClient();
  const [error, setError] = useState(
    browserSupportsWebAuthn() ? '' : 'This browser cannot use Face ID / Touch ID sign-in. Use Safari or Chrome.',
  );
  const [busy, setBusy] = useState(false);
  const [code, setCode] = useState('');
  const [name, setName] = useState(deviceLabel);

  async function attempt(fn: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await fn();
      await qc.invalidateQueries({ queryKey: KEYS.me });
    } catch (e) {
      setError(passkeyErrorText(e));
    }
    setBusy(false);
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2 }}>
      <Card sx={{ width: '100%', maxWidth: 380, p: 3, borderRadius: 4, textAlign: 'center' }}>
        <Box
          component="img"
          src="/icons/logo-full.png"
          alt="SERB"
          sx={{
            width: 210,
            maxWidth: '70%',
            mx: 'auto',
            display: 'block',
            mb: 1,
            bgcolor: '#fff',
            borderRadius: 2,
            p: 1,
          }}
        />
        <Typography color="text.secondary">Ibraheem&apos;s operations app</Typography>
        <Button
          fullWidth
          size="large"
          variant="contained"
          sx={{ mt: 2.5, py: 1.5 }}
          disabled={busy}
          onClick={() => attempt(signIn)}
        >
          Sign in with Face ID / Touch ID
        </Button>
        {error && (
          <Alert severity="error" sx={{ mt: 2, textAlign: 'left' }}>
            {error}
          </Alert>
        )}
        <Accordion
          disableGutters
          elevation={0}
          sx={{ mt: 2.5, bgcolor: 'transparent', '&:before': { display: 'none' } }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
            <Typography color="text.secondary">Add this device</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 0 }}>
            <Stack spacing={1.5} sx={{ textAlign: 'left' }}>
              <Typography variant="body2" color="text.secondary">
                First time on this Mac or iPhone? Make a setup code on the server, then enter it here.
              </Typography>
              <TextField
                label="Setup code"
                placeholder="ABCD-EFGH"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'one-time-code', autoCapitalize: 'characters' } }}
              />
              <TextField label="Name for this device" value={name} onChange={(e) => setName(e.target.value)} />
              <Button
                variant="contained"
                disabled={busy}
                onClick={() => attempt(() => registerDevice({ setupCode: code, deviceName: name }))}
              >
                Set up Face ID / Touch ID
              </Button>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Card>
    </Box>
  );
}
